import React, { useState } from 'react';
import { FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, CheckCircleIcon, ClockIcon, CalendarIcon, TruckIcon, BarChartIcon, PlusIcon, FilterIcon, ShoppingCartIcon, XIcon, CheckIcon, TagIcon, DollarSignIcon, PackageIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
export function ProcurementDashboard({
  requisitions,
  purchaseOrders,
  setCurrentView,
  locale = 'en-US'
}) {
  // Calculate summary statistics
  const totalRequisitions = requisitions.length;
  const pendingApproval = requisitions.filter(req => req.status === 'Pending Approval').length;
  const totalReqSpend = requisitions.reduce((sum, req) => sum + req.total, 0);
  // PO statistics
  const totalPurchaseOrders = purchaseOrders.length;
  const pendingPOs = purchaseOrders.filter(po => po.status === 'Pending Confirmation' || po.status === 'Draft').length;
  const totalPOSpend = purchaseOrders.reduce((sum, po) => sum + po.total, 0);
  // Get recent requisitions (last 3)
  const recentRequisitions = [...requisitions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Get recent POs (last 3)
  const recentPurchaseOrders = [...purchaseOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Sample data for procurement insights
  const pendingTasks = [{
    id: 'TASK-001',
    type: 'Requisition',
    name: 'Office Supplies Approval',
    dueDate: '2023-11-20',
    priority: 'High',
    status: 'Pending Approval'
  }, {
    id: 'TASK-002',
    type: 'Purchase Order',
    name: 'Server Hardware Confirmation',
    dueDate: '2023-11-18',
    priority: 'Medium',
    status: 'Pending Confirmation'
  }, {
    id: 'TASK-003',
    type: 'Receipt',
    name: 'IT Equipment Delivery',
    dueDate: '2023-11-25',
    priority: 'Low',
    status: 'Expected'
  }];
  const upcomingDeliveries = [{
    id: 'PO-2023-001',
    vendor: 'Server Solutions Inc.',
    items: 'Server Hardware',
    expectedDate: '2023-11-22',
    status: 'In Transit',
    trackingNumber: 'TRK123456789'
  }, {
    id: 'PO-2023-002',
    vendor: 'PrintWorks Inc.',
    items: 'Marketing Materials',
    expectedDate: '2023-11-25',
    status: 'Processing',
    trackingNumber: null
  }];
  const topSpendCategories = [{
    category: 'IT Equipment',
    spend: 12500,
    percentage: 40
  }, {
    category: 'Office Supplies',
    spend: 8000,
    percentage: 25
  }, {
    category: 'Marketing',
    spend: 6200,
    percentage: 20
  }, {
    category: 'Services',
    spend: 4800,
    percentage: 15
  }];
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  return <div data-id="element-11496">
      <div className="mb-8 flex justify-between items-center" data-id="element-11497">
        <div data-id="element-11498">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-11499">
            Procurement Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-11500">
            Overview of requisitions, purchase orders, and goods receipts
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-11501">
          <div className="flex items-center" data-id="element-11502">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-11503" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" data-id="element-11504">
              <option value="7days" data-id="element-11505">Last 7 days</option>
              <option value="30days" data-id="element-11506">Last 30 days</option>
              <option value="90days" data-id="element-11507">Last 90 days</option>
              <option value="ytd" data-id="element-11508">Year to date</option>
            </select>
          </div>
          <button onClick={() => setCurrentView('create-requisition')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11509">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-11510" />
            New Requisition
          </button>
        </div>
      </div>
      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-11511">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-11512">
          Procurement Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-11513">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-11514">
            <div className="p-5" data-id="element-11515">
              <div className="flex items-center" data-id="element-11516">
                <div className="flex-shrink-0" data-id="element-11517">
                  <FileTextIcon className="h-6 w-6 text-indigo-400" data-id="element-11518" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-11519">
                  <dl data-id="element-11520">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-11521">
                      Active Requisitions
                    </dt>
                    <dd data-id="element-11522">
                      <div className="text-lg font-medium text-gray-900" data-id="element-11523">
                        {totalRequisitions}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('purchase-orders')} data-id="element-11524">
            <div className="p-5" data-id="element-11525">
              <div className="flex items-center" data-id="element-11526">
                <div className="flex-shrink-0" data-id="element-11527">
                  <ClipboardCheckIcon className="h-6 w-6 text-blue-400" data-id="element-11528" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-11529">
                  <dl data-id="element-11530">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-11531">
                      Active Purchase Orders
                    </dt>
                    <dd data-id="element-11532">
                      <div className="text-lg font-medium text-gray-900" data-id="element-11533">
                        {totalPurchaseOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-11534">
            <div className="p-5" data-id="element-11535">
              <div className="flex items-center" data-id="element-11536">
                <div className="flex-shrink-0" data-id="element-11537">
                  <AlertCircleIcon className="h-6 w-6 text-yellow-400" data-id="element-11538" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-11539">
                  <dl data-id="element-11540">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-11541">
                      Pending Approvals
                    </dt>
                    <dd data-id="element-11542">
                      <div className="text-lg font-medium text-gray-900" data-id="element-11543">
                        {pendingApproval + pendingPOs}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" data-id="element-11544">
            <div className="p-5" data-id="element-11545">
              <div className="flex items-center" data-id="element-11546">
                <div className="flex-shrink-0" data-id="element-11547">
                  <DollarSignIcon className="h-6 w-6 text-green-400" data-id="element-11548" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-11549">
                  <dl data-id="element-11550">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-11551">
                      Total Procurement Spend
                    </dt>
                    <dd data-id="element-11552">
                      <div className="text-lg font-medium text-gray-900" data-id="element-11553">
                        {formatCurrency(totalPOSpend, 'USD', locale)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Procurement Metrics */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-11554">
        {/* Pending Tasks */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-11555">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-11556">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11557">
              Pending Tasks
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-11558">
              {pendingTasks.length} items
            </span>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-11559">
            {pendingTasks.map(task => <li key={task.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => {
            if (task.type === 'Requisition') {
              setCurrentView('requisitions');
            } else if (task.type === 'Purchase Order') {
              setCurrentView('purchase-orders');
            } else {
              setCurrentView('receipting');
            }
          }} data-id="element-11560">
                <div className="flex items-center justify-between" data-id="element-11561">
                  <div className="flex items-center" data-id="element-11562">
                    <div className="flex-shrink-0" data-id="element-11563">
                      {task.type === 'Requisition' ? <FileTextIcon className="h-5 w-5 text-indigo-400" data-id="element-11564" /> : task.type === 'Purchase Order' ? <ClipboardCheckIcon className="h-5 w-5 text-blue-400" data-id="element-11565" /> : <TruckIcon className="h-5 w-5 text-green-400" data-id="element-11566" />}
                    </div>
                    <div className="ml-3" data-id="element-11567">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-11568">
                        {task.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-11569">
                        {task.id} • {task.type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-11570">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.priority === 'High' ? 'bg-red-100 text-red-800' : task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-id="element-11571">
                      {task.priority}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-11572">
                      Due: {task.dueDate}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
        {/* Upcoming Deliveries */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-11573">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-11574">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11575">
              Upcoming Deliveries
            </h3>
            <button onClick={() => setCurrentView('receipting')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-11576">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-11577">
            {upcomingDeliveries.map(delivery => <li key={delivery.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('receipting')} data-id="element-11578">
                <div className="flex items-center justify-between" data-id="element-11579">
                  <div className="flex items-center" data-id="element-11580">
                    <div className="flex-shrink-0" data-id="element-11581">
                      <TruckIcon className="h-5 w-5 text-blue-400" data-id="element-11582" />
                    </div>
                    <div className="ml-3" data-id="element-11583">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-11584">
                        {delivery.items}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-11585">
                        {delivery.id} • {delivery.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-11586">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${delivery.status === 'In Transit' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-11587">
                      {delivery.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-11588">
                      Expected: {delivery.expectedDate}
                    </span>
                  </div>
                </div>
                {delivery.trackingNumber && <div className="mt-2 text-xs text-gray-500" data-id="element-11589">
                    Tracking: {delivery.trackingNumber}
                  </div>}
              </li>)}
          </ul>
        </div>
      </div>
      {/* Spend by Category */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-11590">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-11591">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11592">
            Spend by Category
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-11593">
          <div className="space-y-4" data-id="element-11594">
            {topSpendCategories.map(category => <div key={category.category} data-id="element-11595">
                <div className="flex items-center justify-between mb-1" data-id="element-11596">
                  <div className="flex items-center" data-id="element-11597">
                    <TagIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-11598" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-11599">
                      {category.category}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-11600">
                    {formatCurrency(category.spend, 'USD', locale)} (
                    {category.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-11601">
                  <div className="bg-indigo-600 h-2.5 rounded-full" style={{
                width: `${category.percentage}%`
              }} data-id="element-11602"></div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* Recent Requisitions and Purchase Orders */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-11603">
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-11604">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-11605">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11606">
              Recent Requisitions
            </h3>
            <button onClick={() => setCurrentView('requisitions')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-11607">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-11608">
            {recentRequisitions.map(req => <li key={req.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('requisitions')} data-id="element-11609">
                <div className="flex items-center justify-between" data-id="element-11610">
                  <div className="flex items-center" data-id="element-11611">
                    <div className="flex-shrink-0" data-id="element-11612">
                      <FileTextIcon className="h-5 w-5 text-indigo-400" data-id="element-11613" />
                    </div>
                    <div className="ml-3" data-id="element-11614">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-11615">
                        {req.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-11616">
                        {req.id} • {req.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-11617">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${req.status === 'Approved' ? 'bg-green-100 text-green-800' : req.status === 'Draft' ? 'bg-gray-100 text-gray-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-11618">
                      {req.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-11619">
                      {formatCurrency(req.total, 'USD', locale)}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-11620">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-11621">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11622">
              Recent Purchase Orders
            </h3>
            <button onClick={() => setCurrentView('purchase-orders')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-11623">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-11624">
            {recentPurchaseOrders.map(po => <li key={po.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('purchase-orders')} data-id="element-11625">
                <div className="flex items-center justify-between" data-id="element-11626">
                  <div className="flex items-center" data-id="element-11627">
                    <div className="flex-shrink-0" data-id="element-11628">
                      <ClipboardCheckIcon className="h-5 w-5 text-blue-400" data-id="element-11629" />
                    </div>
                    <div className="ml-3" data-id="element-11630">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-11631">
                        {po.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-11632">
                        {po.id} • {po.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-11633">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${po.status === 'Sent to Vendor' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-11634">
                      {po.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-11635">
                      {formatCurrency(po.total, 'USD', locale)}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
      </div>
      {/* Procurement KPIs */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-11636">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-11637">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11638">
            Procurement KPIs
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-11639">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-11640">
            <div className="bg-green-50 p-4 rounded-lg" data-id="element-11641">
              <div className="flex items-center" data-id="element-11642">
                <div className="flex-shrink-0 bg-green-100 rounded-md p-2" data-id="element-11643">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-11644" />
                </div>
                <div className="ml-4" data-id="element-11645">
                  <h4 className="text-sm font-medium text-green-900" data-id="element-11646">
                    On-Time Delivery
                  </h4>
                  <p className="text-3xl font-semibold text-green-700" data-id="element-11647">92%</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg" data-id="element-11648">
              <div className="flex items-center" data-id="element-11649">
                <div className="flex-shrink-0 bg-blue-100 rounded-md p-2" data-id="element-11650">
                  <ClockIcon className="h-6 w-6 text-blue-600" data-id="element-11651" />
                </div>
                <div className="ml-4" data-id="element-11652">
                  <h4 className="text-sm font-medium text-blue-900" data-id="element-11653">
                    Avg. Processing Time
                  </h4>
                  <p className="text-3xl font-semibold text-blue-700" data-id="element-11654">
                    3.2 days
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg" data-id="element-11655">
              <div className="flex items-center" data-id="element-11656">
                <div className="flex-shrink-0 bg-indigo-100 rounded-md p-2" data-id="element-11657">
                  <PackageIcon className="h-6 w-6 text-indigo-600" data-id="element-11658" />
                </div>
                <div className="ml-4" data-id="element-11659">
                  <h4 className="text-sm font-medium text-indigo-900" data-id="element-11660">
                    Order Accuracy
                  </h4>
                  <p className="text-3xl font-semibold text-indigo-700" data-id="element-11661">98%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}